import AddIcon from "@mui/icons-material/Add";
import { Modal, Button, Paper, Grid, Typography, Box, Alert, TextField, ButtonGroup, useTheme } from "@mui/material";
import { useState, useCallback, useEffect } from "react";
import LoadingWrapper from "src/components/LoadingWrapper/LoadingWrapper";
import { DraggableListItem } from "src/components/DraggableListItem/DraggableListItem";
import CheckIcon from "@mui/icons-material/Check";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { useDispatch } from "react-redux";
import { ISimpleUser } from "@vmosolutions/oc-interfaces";

interface ISimpleUserWithSelected extends ISimpleUser {
  isSelected: boolean;
}

export const UserDeviceInviteModal = (): JSX.Element => {
  const theme = useTheme();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [users, setUsers] = useState<ISimpleUserWithSelected[]>([]);
  const inviteLimit = 10;
  const [loadingUser, setLoadingUser] = useState<boolean>(true);
  const [filterValue, setFilterValue] = useState<string>("");
  const dispatch = useDispatch();

  const emailInvitation = useCallback(
    async (userId: string) => {
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: "Creating Invitation",
        successMessage: "Invitation Sent",
        errorMessage: "Error: Unable To generate invitation",
      };
      const response = await AmplifyAPIWrapper.Post<any>(`/user/iosdevice/invite/${userId}`, notifications);
      if (response) {
        return true;
      } else {
        return false;
      }
    },
    [dispatch]
  );

  const resetUsers = useCallback(() => {
    const userMemberList = [...users];
    setUsers(
      userMemberList.map((user: ISimpleUserWithSelected) => {
        user.isSelected = false;
        return user;
      })
    );
  }, [users]);

  const handleBulkInvite = useCallback(() => {
    const userToInvite = users.filter((user: ISimpleUserWithSelected) => {
      return user.isSelected === true;
    });
    userToInvite.forEach((user: ISimpleUserWithSelected): void => {
      emailInvitation(user._id);
    });
    resetUsers();
    setModalOpen(false);
  }, [users, emailInvitation, resetUsers]);

  useEffect(() => {
    const getUsers = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.GetPaginate<ISimpleUser>("/users?simpleUser=true", null);
      if (Array.isArray(response)) {
        const userList = response.map((user: ISimpleUser) => {
          const userWithIsSelected = user as ISimpleUserWithSelected;
          userWithIsSelected.isSelected = false;
          return userWithIsSelected;
        });
        setUsers(userList);
        setLoadingUser(false);
      }
    };
    setLoadingUser(true);
    getUsers();
  }, []);

  const updateInviteList = useCallback(
    (_id: string) => {
      const userToUpdate = users.findIndex((user: ISimpleUserWithSelected) => {
        return user._id === _id;
      });
      const newUserList = [...users];
      const selectedUser = newUserList.filter((user: ISimpleUserWithSelected) => {
        return user.isSelected === true;
      }).length;

      if (selectedUser < inviteLimit || newUserList[userToUpdate].isSelected === true) {
        newUserList[userToUpdate].isSelected = !newUserList[userToUpdate].isSelected;
        setUsers(newUserList);
      }
    },
    [users]
  );

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={() => {
          setModalOpen(!modalOpen);
        }}
      >
        <AddIcon />
        Invite Users
      </Button>
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="modal-usermember-list"
        aria-describedby="modal-usermember-list-all"
      >
        <Paper sx={{ width: 800, margin: "auto", p: 2 }}>
          <Grid container>
            <Grid item xs={12} sx={{ p: 3, pb: 1 }}>
              <Alert severity="info" variant="filled">
                Please note that you can invite up to {inviteLimit} people at a time.
              </Alert>
            </Grid>
            <Grid item xs={12} sx={{ p: 3, pb: 1 }}>
              {loadingUser ? (
                <LoadingWrapper message="Loading User list" />
              ) : (
                <>
                  <Typography component="h2">
                    Users to invite (
                    {
                      users.filter((user) => {
                        return (!user.hasOwnProperty("crewInfo") || user.crewInfo === null) && user.active === true;
                      }).length
                    }{" "}
                    total)
                  </Typography>
                  <TextField
                    size="small"
                    sx={{
                      width: "100%",
                      border: `1px solid ${theme.custom.forms.inputBorder}`,
                      background: theme.custom.forms.inputBack,
                    }}
                    placeholder="Filter Users"
                    variant="outlined"
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.target.value)}
                  />
                  <Box sx={{ height: "60vh", overflow: "scroll" }}>
                    {users
                      .filter((user: ISimpleUser) => {
                        const filter = filterValue.toLowerCase().replace(/[^a-zA-Z0-9\s]/g, "");
                        return (
                          (!user.hasOwnProperty("crewInfo") || user.crewInfo === null) &&
                          user.active === true &&
                          (user.firstName.toLowerCase().includes(filter) ||
                            user.lastName.toLowerCase().includes(filter) ||
                            (user?.employeeId && user.employeeId.includes(filter)))
                        );
                      })
                      .sort((a, b) => {
                        return a.lastName.localeCompare(b.lastName);
                      })
                      .map((user, key) => {
                        return (
                          <DraggableListItem
                            active={user.isSelected ? true : false}
                            key={key}
                            onClick={() => updateInviteList(user._id)}
                            sx={{ display: "flex", justifyContent: "space-between", cursor: "pointer" }}
                          >
                            {user.lastName}, {user.firstName} {user.employeeId ? `(${user.employeeId})` : ""}
                            {user.isSelected && (
                              <Box sx={{ display: "flex", justifySelf: "end" }}>
                                <CheckIcon />
                              </Box>
                            )}
                          </DraggableListItem>
                        );
                      })}
                  </Box>
                </>
              )}
            </Grid>
          </Grid>
          <Grid container>
            <Grid item sx={{ maxHeight: "70vh", overflowY: "scroll", width: "100%", mb: 2 }}></Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12} sx={{ display: "flex", justifyContent: "end" }}>
              <ButtonGroup variant="contained">
                <Button color="success" sx={{ color: "white", alignSelf: "end" }} onClick={() => handleBulkInvite()}>
                  Send Invitation to{" "}
                  {
                    users.filter((user: ISimpleUserWithSelected) => {
                      return user?.isSelected === true;
                    }).length
                  }{" "}
                  User Member(s)
                </Button>
                <Button
                  color="error"
                  sx={{ alignSelf: "end" }}
                  onClick={() => {
                    resetUsers();
                    setModalOpen(false);
                  }}
                >
                  Cancel
                </Button>
              </ButtonGroup>
            </Grid>
          </Grid>
        </Paper>
      </Modal>
    </>
  );
};
